import { AsyncPipe, NgIf } from '@angular/common';
import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorDirective, ErrorsDirective } from '@shared/directives/errors';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { NotificationExpandableCardComponent } from '@shared/shared/components/notification-expandable-card/notification-expandable-card.component';
import { BaseComponent } from '@shared/shared/helpers/base-component';
import { NotificationService } from '@shared/shared/modules/notification/services/notification.service';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import * as MemberProfileStore from '@shared/store/member-profile';
import { UserLogout } from '@shared/store/user';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ten-complete-member-profile-popup',
  templateUrl: './complete-member-profile-popup.component.html',
  styleUrls: ['./complete-member-profile-popup.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    TranslateModule,
    ReactiveFormsModule,
    NgIf,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    ErrorDirective,
    ErrorsDirective,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    NotificationExpandableCardComponent,
    MatDialogModule,
  ],
})
export class CompleteMemberProfilePopupComponent extends BaseComponent implements OnInit, OnDestroy {
  public customisableVariables$: Observable<CustomisableVariables> = this.customVariablesService.variables;
  public loading = false;
  public needsTitle = false;
  public needsTerms = false;
  public needEmail = false;
  public profileFormGroup: UntypedFormGroup;
  public data: any;

  public componentData: {
    isEmailRequired: boolean;
    showFirstName: boolean;
    showLastName: boolean;
    showCreditSaisonPotags?: boolean;
    useBottomSheet?: boolean;
  };

  private unsubscribe$ = new Subject<void>();
  public popupRef: any; // Generic reference that can be either dialog or bottom sheet

  constructor(
    private customVariablesService: CustomisableVariablesService,
    private ngrxStore: Store<MemberProfileStore.MemberProfileState>,
    private formBuilder: UntypedFormBuilder,
    private notificationService: NotificationService,
    siteConf: SiteConfigService,
    private injector: Injector
  ) {
    super(siteConf, 'CompleteMemberProfile');
  }

  public ngOnInit() {
    this.profileFormGroup = this.formBuilder.group({});

    // Try to get dialog references first, then bottom sheet
    try {
      const dialogRef = this.injector.get(MatDialogRef, null);
      if (dialogRef) {
        this.popupRef = dialogRef;
        this.data = this.injector.get(MAT_DIALOG_DATA, {});
      }
    } catch (e) {
      // Ignore error, try bottom sheet
    }

    if (!this.popupRef) {
      try {
        const bottomSheetRef = this.injector.get(MatBottomSheetRef, null);
        if (bottomSheetRef) {
          this.popupRef = bottomSheetRef;
          this.data = this.injector.get(MAT_BOTTOM_SHEET_DATA, {});
        }
      } catch (e) {
        // Ignore error
        console.warn('CompleteMemberProfilePopupComponent opened without proper dialog/bottomsheet context');
        this.data = {};
      }
    }

    if (this.data?.title === '' || this.data?.title === null) {
      this.needsTitle = true;
      this.profileFormGroup.addControl('title', this.formBuilder.control('', Validators.required));
    } else {
      this.profileFormGroup.addControl('title', this.formBuilder.control(''));
      this.profileFormGroup.get('title')?.setValue(this.data.title);
    }

    if (this.componentData?.isEmailRequired && (this.data.email === '' || this.data.email === null)) {
      this.needEmail = true;
      this.profileFormGroup.addControl('email', this.formBuilder.control('', [Validators.required, Validators.email]));
    }

    if (!this.data.termsAndConditions) {
      this.needsTerms = true;
      if (!this.data.tenOptIn) {
        this.profileFormGroup.addControl('ten_opt_in', this.formBuilder.control(false));
      }
      this.profileFormGroup.addControl(
        'terms_and_conditions',
        this.formBuilder.control(false, Validators.requiredTrue)
      );
    }
  }

  public updateProfile() {
    this.loading = true;

    this.ngrxStore.dispatch(new MemberProfileStore.UpdateMemberProfile(this.profileFormGroup.value));
    this.ngrxStore
      .select(MemberProfileStore.getMemberProfileLoaded)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (res) {
          this.loading = false;
          console.log('Profile updated successfully', this.popupRef);
          if (this.popupRef) {
            if ('close' in this.popupRef) {
              this.popupRef.close(); // MatDialogRef
            } else if ('dismiss' in this.popupRef) {
              this.popupRef.dismiss(); // MatBottomSheetRef
            }
          }

          if (this.componentData?.showCreditSaisonPotags) {
            this.notificationService.showNotification('TermsPopup:InformationCaptureMessage');
          }
        } else {
          if (this.componentData?.showCreditSaisonPotags) {
            this.loading = false;
          }
        }
      });

    this.ngrxStore
      .select(MemberProfileStore.getMemberProfileErrorsFeatureState)
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((res: any) => Array.isArray(res) && res.length > 0 && res[0] && typeof res[0] === 'object')
      )
      .subscribe((res: any) => {
        if (res && res.length > 0) {
          this.loading = false;
          const emailControl = this.profileFormGroup.get('email');
          if (res[0].code === 'email_not_unique' || res[0].error === 'email_not_unique') {
            emailControl.setErrors({
              ...emailControl.errors,
              retry: true,
            });
          } else {
            if (emailControl?.hasError('retry')) {
              const errors = { ...emailControl.errors };
              delete errors['retry'];
              emailControl.setErrors(Object.keys(errors).length ? errors : null);
            }
          }
        }
      });
  }

  public logOut() {
    this.ngrxStore.dispatch(new UserLogout());
    if (this.popupRef) {
      if ('close' in this.popupRef) {
        this.popupRef.close(); // MatDialogRef
      } else if ('dismiss' in this.popupRef) {
        this.popupRef.dismiss(); // MatBottomSheetRef
      }
    }
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
}
